import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  FaBoxOpen, FaBrain, FaCheckCircle, FaChevronDown, FaChevronRight,
  FaExclamationTriangle, FaPlug, FaSearch, FaSpinner, FaSync, FaTimesCircle,
} from 'react-icons/fa';
import './ParcelMonitor.css';

const PAGE_SIZES = [25, 50, 100];
const STATUS_VALUES = ['pending', 'in_transit', 'delivered', 'returned', 'problem', 'silent_lost'];
// Calibrated operating points of the risk models (see docs/parcel-monitor-spec.md).
const THRESHOLD_OPTIONS = ['0.5', '0.7', '0.85', '0.95'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const pct = (value) => (value == null ? '—' : `${Math.round(value * 100)} %`);

// The shared risk models' precision (closed flags confirmed by the parcel's
// outcome) across every account and for this one, its 12-week trend, and how
// many labelled parcels are still needed before the next training run.
function ModelLearning({ learning }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const platform = learning.precision || {};
  const account = learning.account_precision || {};
  const next = learning.next_training || {};
  const trend = learning.trend || [];
  const model = learning.model || {};
  const fmt = (n) => Number(n || 0).toLocaleString(lang);
  const percent = Math.round((next.progress || 0) * 1000) / 10;
  return (
    <section className="pm-card pm-learning" aria-label={t('parcelMonitor.learning.title', 'Apprentissage du modèle')}>
      <div className="pm-learning-head">
        <h3><FaBrain /> {t('parcelMonitor.learning.title', 'Apprentissage du modèle')}</h3>
        <span className="pm-muted">
          {t('parcelMonitor.learning.model', 'Modèle {{version}} · entraîné sur {{n}} colis (export externe {{period}})', {
            version: model.version || 'v1', n: fmt(model.trained_on?.shipments), period: model.trained_on?.period || '',
          })}
        </span>
      </div>
      <div className="pm-learning-grid">
        <div className="pm-learning-precision">
          <span className="pm-learning-value">{pct(platform.precision)}</span>
          <span className="pm-learning-label">{t('parcelMonitor.learning.precision', 'précision sur 30 jours, tous comptes')}</span>
          <span className="pm-muted pm-small">
            {platform.flags_closed
              ? t('parcelMonitor.learning.precisionBasis', '{{confirmed}} alertes confirmées sur {{n}} closes', { confirmed: fmt(platform.confirmed), n: fmt(platform.flags_closed) })
              : t('parcelMonitor.learning.noFlag', 'Aucune alerte close sur la période : la précision s’affiche dès la première.')}
          </span>
          <span className="pm-learning-account">
            {t('parcelMonitor.learning.account', 'Votre compte : {{value}}', { value: pct(account.precision) })}
            {account.flags_closed ? ` (${fmt(account.flags_closed)})` : ''}
          </span>
        </div>
        <div className="pm-learning-trend">
          <span className="pm-learning-label">{t('parcelMonitor.learning.trend', 'Évolution hebdomadaire (12 semaines)')}</span>
          <div className="pm-trend" role="img" aria-label={t('parcelMonitor.learning.trend', 'Évolution hebdomadaire (12 semaines)')}>
            {trend.map((point) => (
              <div key={point.week} className="pm-trend-col"
                title={`${point.week} · ${point.precision == null ? t('parcelMonitor.learning.noData', 'pas d’alerte close') : `${pct(point.precision)} (${point.flags_closed})`}`}>
                <div className={`pm-trend-bar ${point.precision == null ? 'is-empty' : ''}`}
                  style={{ height: point.precision == null ? '2px' : `${Math.max(4, point.precision * 100)}%` }} />
              </div>
            ))}
          </div>
        </div>
        <div className="pm-learning-next">
          <div className="pm-learning-next-head">
            <span className="pm-learning-label">{t('parcelMonitor.learning.nextTraining', 'Prochain entraînement')}</span>
            <strong>{percent.toLocaleString(lang)} %</strong>
          </div>
          <div className="pm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <div className={`pm-progress-fill ${next.ready ? 'is-ready' : ''}`} style={{ width: `${Math.max(percent, next.collected ? 0.5 : 0)}%` }} />
          </div>
          <span className="pm-small">
            {next.ready
              ? t('parcelMonitor.learning.ready', 'Prêt : {{n}} colis étiquetés, assez pour réentraîner les modèles.', { n: fmt(next.collected) })
              : t('parcelMonitor.learning.progress', '{{collected}} / {{target}} colis terminés et étiquetés', { collected: fmt(next.collected), target: fmt(next.target) })}
          </span>
          <span className="pm-muted pm-small">
            {t('parcelMonitor.learning.composition', 'dont {{positives}} problèmes réels et {{flags}} alertes closes · tous comptes', {
              positives: fmt(next.positives), flags: fmt(next.flags_closed),
            })}
          </span>
        </div>
      </div>
    </section>
  );
}

function ParcelMonitor() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- list state (kept in the URL) ----
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const size = PAGE_SIZES.includes(parseInt(searchParams.get('size'), 10))
    ? parseInt(searchParams.get('size'), 10) : 25;
  const statusFilter = searchParams.get('status') || '';
  const flaggedOnly = searchParams.get('flagged') === '1';
  const urlQuery = searchParams.get('q') || '';

  const [searchInput, setSearchInput] = useState(urlQuery);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  // Resolve dialog: manual resolution must capture WHY (feeds the precision
  // metrics and the retraining archive).
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveReason, setResolveReason] = useState('false_alarm');
  const [resolveNote, setResolveNote] = useState('');

  const [proactive, setProactive] = useState(null);

  // ---- config state ----
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState(null);
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  // ---- model learning (precision + next training run) ----
  const [learning, setLearning] = useState(null);

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    }
  }, []);

  useEffect(() => () => feedbackTimer.current && clearTimeout(feedbackTimer.current), []);

  const updateListParams = useCallback((updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || value === false) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  // ---- data loading ----
  const loadList = useCallback(async () => {
    try {
      const params = { page, size };
      if (statusFilter) params.status = statusFilter;
      if (flaggedOnly) params.flagged = true;
      if (urlQuery) params.q = urlQuery;
      const res = await axios.get('/api/parcel-monitor/parcels/list', { params, headers: authHeaders() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load parcels', error);
      setItems([]);
      setTotal(0);
    }
  }, [page, size, statusFilter, flaggedOnly, urlQuery]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/parcels/stats', { headers: authHeaders() });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load parcel stats', error);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/config', { headers: authHeaders() });
      setConfig(res.data);
      setConfigDraft({
        enabled: res.data.enabled,
        mode: res.data.mode,
        tracking_provider: res.data.tracking_provider,
        flag_threshold: res.data.flag_threshold === res.data.default_flag_threshold
          ? '' : String(res.data.flag_threshold),
        external_sync: res.data.external_sync !== false,
        edn_ticket_topic_id: res.data.edn_ticket_topic_id == null ? '' : String(res.data.edn_ticket_topic_id),
      });
      setConfigDirty(false);
    } catch (error) {
      console.error('Failed to load parcel monitor config', error);
    }
  }, []);

  const loadProactive = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/analysis/proactive', { headers: authHeaders() });
      setProactive(res.data);
    } catch (error) {
      console.error('Failed to load proactive analysis', error);
    }
  }, []);

  const loadLearning = useCallback(async () => {
    try {
      const res = await axios.get('/api/parcel-monitor/model/learning', { headers: authHeaders() });
      setLearning(res.data);
    } catch (error) {
      console.error('Failed to load model learning status', error);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); loadConfig(); loadProactive(); loadLearning(); }, [loadStats, loadConfig, loadProactive, loadLearning]);

  // Debounced tracking-number search -> URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== urlQuery) {
        updateListParams({ q: searchInput, page: '' }, { replace: true });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, urlQuery, updateListParams]);

  // ---- config actions ----
  const markConfigDirty = (updates) => {
    setConfigDraft((prev) => ({ ...prev, ...updates }));
    setConfigDirty(true);
    setFeedback(null);
  };

  const saveConfig = async () => {
    if (!configDraft) return;
    setConfigSaving(true);
    try {
      const payload = {
        enabled: configDraft.enabled,
        mode: configDraft.mode,
        tracking_provider: configDraft.tracking_provider,
        flag_threshold: configDraft.flag_threshold === '' ? null : Number(configDraft.flag_threshold),
        external_sync: configDraft.external_sync,
        edn_ticket_topic_id: configDraft.edn_ticket_topic_id === '' ? null : Number(configDraft.edn_ticket_topic_id),
      };
      const res = await axios.put('/api/parcel-monitor/config', payload, { headers: authHeaders() });
      setConfig(res.data);
      setConfigDirty(false);
      showFeedback('success', t('parcelMonitor.configSaved', 'Configuration enregistrée.'));
    } catch (error) {
      const detail = error.response?.data?.detail;
      showFeedback('error', typeof detail === 'string'
        ? detail : t('parcelMonitor.configSaveError', "Impossible d'enregistrer la configuration."));
    } finally {
      setConfigSaving(false);
    }
  };

  // ---- row actions ----
  const toggleExpand = async (parcelId) => {
    if (expandedId === parcelId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(parcelId);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`/api/parcel-monitor/parcels/${parcelId}`, { headers: authHeaders() });
      setExpandedDetail(res.data);
    } catch (error) {
      console.error('Failed to load parcel detail', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshParcelTracking = async (parcelId) => {
    setActionBusy(parcelId + 'refresh');
    try {
      await axios.post(`/api/parcel-monitor/parcels/${parcelId}/refresh`, {}, { headers: authHeaders() });
      // The refresh runs in a background worker; re-read after a short delay.
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/parcel-monitor/parcels/${parcelId}`, { headers: authHeaders() });
          setExpandedDetail((prev) => (expandedId === parcelId ? res.data : prev));
          loadList();
        } catch (error) { console.error('Refresh reload failed', error); }
        setActionBusy(null);
      }, 6000);
    } catch (error) {
      console.error('Parcel refresh failed', error);
      setActionBusy(null);
    }
  };

  const alertAction = async (parcelId, action, body = {}) => {
    setActionBusy(parcelId + action);
    try {
      await axios.post(`/api/parcel-monitor/parcels/${parcelId}/alert/${action}`, body, { headers: authHeaders() });
      await Promise.all([loadList(), loadStats()]);
      if (expandedId === parcelId) {
        const res = await axios.get(`/api/parcel-monitor/parcels/${parcelId}`, { headers: authHeaders() });
        setExpandedDetail(res.data);
      }
    } catch (error) {
      console.error(`Alert ${action} failed`, error);
    } finally {
      setActionBusy(null);
    }
  };

  // ---- rendering helpers ----
  const statusLabel = (value) => t(`parcelMonitor.status.${value}`, value);

  const riskBadge = (risk) => {
    if (!risk) return null;
    if (risk.flagged) {
      return (
        <span className="pm-badge pm-badge--danger" title={risk.flag_reason || ''}>
          <FaExclamationTriangle /> {t('parcelMonitor.risk.flagged', 'À risque')}
        </span>
      );
    }
    const score = Math.max(risk.stall_score || 0, risk.hazard_score || 0);
    if (score >= 0.4) {
      return <span className="pm-badge pm-badge--warning">{t('parcelMonitor.risk.watch', 'À surveiller')} · {(score * 100).toFixed(0)}%</span>;
    }
    if (risk.stall_score !== null || risk.hazard_score !== null) {
      return <span className="pm-badge pm-badge--ok">{t('parcelMonitor.risk.ok', 'Normal')}</span>;
    }
    return (
      <span className="pm-badge" title={t('parcelMonitor.risk.awaitingHint', 'Le score est calculé dès la première remontée de suivi (cycle horaire).')}>
        {t('parcelMonitor.risk.awaitingFirstScan', 'En attente du 1er suivi')}
      </span>
    );
  };

  const alertBadge = (alert) => {
    if (!alert || alert.state === 'none') return <span className="pm-muted">—</span>;
    const labels = {
      open: t('parcelMonitor.alert.open', 'Alerte ouverte'),
      acknowledged: t('parcelMonitor.alert.acknowledged', 'Prise en compte'),
      resolved: t('parcelMonitor.alert.resolved', 'Résolue'),
    };
    const cls = alert.state === 'open' ? 'pm-badge--danger'
      : alert.state === 'acknowledged' ? 'pm-badge--warning' : 'pm-badge--ok';
    return <span className={`pm-badge ${cls}`}>{labels[alert.state] || alert.state}</span>;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch (error) {
      return iso;
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / size));
  const pageItems = useMemo(() => {
    const pages = [];
    const push = (value) => pages[pages.length - 1] !== value && pages.push(value);
    push(1);
    for (let i = page - 1; i <= page + 1; i += 1) {
      if (i > 1 && i < totalPages) {
        if (pages[pages.length - 1] !== i - 1) push('…');
        push(i);
      }
    }
    if (totalPages > 1) {
      if (pages[pages.length - 1] !== totalPages - 1 && pages[pages.length - 1] !== '…' && totalPages - 1 > 1) push('…');
      push(totalPages);
    }
    return pages;
  }, [page, totalPages]);

  const loading = items === null;

  return (
    <div className="parcel-monitor-page">
      <div className="pm-header">
        <h1><FaBoxOpen /> {t('parcelMonitor.title', 'Surveillance des colis')} <span className="pm-beta">beta</span></h1>
        <p className="pm-subtitle">
          {t('parcelMonitor.subtitle',
            "Tous vos colis suivis en continu : l'IA détecte les colis à risque avant que le destinataire ne s'en aperçoive.")}
        </p>
      </div>

      {feedback && (
        <div className={`pm-feedback ${feedback.type}`} role="status">
          {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* ---- stats ---- */}
      {stats && (
        <div className="pm-stats">
          <div className="pm-stat"><span className="pm-stat-value">{stats.total}</span><span className="pm-stat-label">{t('parcelMonitor.stats.total', 'Colis suivis')}</span></div>
          <div className="pm-stat"><span className="pm-stat-value">{stats.by_status?.in_transit || 0}</span><span className="pm-stat-label">{t('parcelMonitor.stats.inTransit', 'En transit')}</span></div>
          <div className="pm-stat pm-stat--danger"><span className="pm-stat-value">{stats.open_alerts}</span><span className="pm-stat-label">{t('parcelMonitor.stats.openAlerts', 'Alertes ouvertes')}</span></div>
          <div className="pm-stat"><span className="pm-stat-value">{stats.flagged}</span><span className="pm-stat-label">{t('parcelMonitor.stats.flagged', 'Colis signalés')}</span></div>
          {stats.models && !stats.models.loaded && (
            <div className="pm-stat pm-stat--danger">
              <span className="pm-stat-value"><FaExclamationTriangle /></span>
              <span className="pm-stat-label">{t('parcelMonitor.stats.modelsDown', 'Modèles non chargés')}</span>
            </div>
          )}
        </div>
      )}

      {/* ---- model: precision (all accounts + this one), trend, next training run ---- */}
      {learning && <ModelLearning learning={learning} />}

      {/* ---- proactive validation: customer tickets vs flags ---- */}
      {proactive && proactive.tickets_on_monitored_parcels > 0 && (
        <section className="pm-card pm-proactive">
          <div className="pm-card-body pm-proactive-body">
            <h3>{t('parcelMonitor.proactive.title', 'Validation proactive ({{days}}j)', { days: proactive.window_days })}</h3>
            <p className="pm-hint">
              {t('parcelMonitor.proactive.hint',
                'Tickets clients réellement ouverts sur des colis suivis, comparés aux signalements du modèle.')}
            </p>
            <div className="pm-proactive-grid">
              <div className="pm-stat">
                <span className="pm-stat-value">{proactive.tickets_on_monitored_parcels}</span>
                <span className="pm-stat-label">{t('parcelMonitor.proactive.tickets', 'Tickets clients sur colis suivis')}</span>
              </div>
              <div className="pm-stat">
                <span className="pm-stat-value" style={{ color: 'var(--state-success)' }}>{proactive.preempted}</span>
                <span className="pm-stat-label">
                  {proactive.median_lead_h != null
                    ? t('parcelMonitor.proactive.preemptedWithLead', 'Anticipables (avance médiane {{h}}h)', { h: proactive.median_lead_h })
                    : t('parcelMonitor.proactive.preempted', 'Anticipables (signalés avant le ticket)')}
                </span>
              </div>
              <div className="pm-stat">
                <span className="pm-stat-value">{proactive.late}</span>
                <span className="pm-stat-label">{t('parcelMonitor.proactive.late', 'Signalés trop tard')}</span>
              </div>
              <div className="pm-stat">
                <span className="pm-stat-value" style={{ color: 'var(--state-danger)' }}>{proactive.missed}</span>
                <span className="pm-stat-label">{t('parcelMonitor.proactive.missed', 'Manqués (aucun signalement)')}</span>
              </div>
            </div>
            {proactive.preemptable_share != null && (
              <p className="pm-proactive-conclusion">
                {t('parcelMonitor.proactive.conclusion',
                  '{{pct}}% des tickets clients auraient pu être ouverts proactivement avec les actions automatiques activées.',
                  { pct: (proactive.preemptable_share * 100).toFixed(0) })}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ---- configuration ---- */}
      {config && configDraft && (
        <section className="pm-card">
          <button
            type="button"
            className="pm-card-header"
            aria-expanded={configOpen}
            onClick={() => setConfigOpen((open) => !open)}
          >
            <span className="pm-card-title">{t('parcelMonitor.config.title', 'Configuration')}</span>
            <span className={`pm-badge ${config.enabled ? 'pm-badge--ok' : ''}`}>
              {config.enabled ? t('common.on', 'Activé') : t('common.off', 'Désactivé')}
            </span>
            {configOpen ? <FaChevronDown /> : <FaChevronRight />}
          </button>

          {configOpen && (
            <div className="pm-card-body">
              <div className="pm-form-grid">
                <div className="pm-form-row">
                  <label>{t('parcelMonitor.config.enabled', 'Surveillance activée')}</label>
                  <button
                    type="button" role="switch" aria-checked={configDraft.enabled}
                    className={`pm-switch ${configDraft.enabled ? 'on' : ''}`}
                    onClick={() => markConfigDirty({ enabled: !configDraft.enabled })}
                  ><span className="pm-switch-knob" /></button>
                </div>

                <div className="pm-form-row">
                  <label>{t('parcelMonitor.config.provider', 'Source de suivi')}</label>
                  <select
                    value={configDraft.tracking_provider}
                    onChange={(e) => markConfigDirty({ tracking_provider: e.target.value })}
                  >
                    <option value="edn">{t('parcelMonitor.config.providerEdn', 'Envoi du Net (votre compte)')}</option>
                    <option value="ship24">{t('parcelMonitor.config.providerShip24', 'Ship24')}</option>
                  </select>
                  {configDraft.tracking_provider === 'edn' && !config.edn_credentials_configured && (
                    <p className="pm-hint pm-hint--warn">
                      {t('parcelMonitor.config.ednCredentialsMissing',
                        "Identifiants API Envoi du Net manquants sur votre compte — contactez le support pour les configurer.")}
                    </p>
                  )}
                </div>

                <div className="pm-form-row">
                  <label>{t('parcelMonitor.config.mode', 'Action en cas de risque')}</label>
                  <select
                    value={configDraft.mode}
                    onChange={(e) => markConfigDirty({ mode: e.target.value })}
                  >
                    <option value="alert_only">{t('parcelMonitor.config.modeAlertOnly', 'Alerte uniquement')}</option>
                    <option value="create_ticket">{t('parcelMonitor.config.modeCreateTicket', 'Alerte + création de ticket')}</option>
                    <option value="auto_contact">{t('parcelMonitor.config.modeAutoContact', 'Alerte + ticket + contact transporteur automatique')}</option>
                  </select>
                  {configDraft.mode === 'auto_contact' && (
                    <p className="pm-hint">
                      {t('parcelMonitor.config.autoContactHint',
                        "L'IA rédige et envoie une demande au transporteur via le canal configuré (portail, email ou formulaire). Si l'automatisation du client est désactivée, le message attend une validation humaine.")}
                    </p>
                  )}
                  {configDraft.mode === 'create_ticket' && (
                    <p className="pm-hint">
                      {t('parcelMonitor.config.createTicketHint',
                        "Un ticket est ouvert automatiquement pour le client concerné (résolu via ses alias) et synchronisé vers son système externe si configuré.")}
                    </p>
                  )}
                </div>

                {configDraft.mode !== 'alert_only' && (
                  <div className="pm-form-row">
                    <label>{t('parcelMonitor.config.externalSync', 'Synchroniser vers le système externe (EDN, Spacefill…)')}</label>
                    <button
                      type="button" role="switch" aria-checked={configDraft.external_sync}
                      className={`pm-switch ${configDraft.external_sync ? 'on' : ''}`}
                      onClick={() => markConfigDirty({ external_sync: !configDraft.external_sync })}
                    ><span className="pm-switch-knob" /></button>
                    {configDraft.external_sync && (
                      <>
                        <label className="pm-sublabel">{t('parcelMonitor.config.ednTopicId', 'ID de topic EDN pour les tickets créés')}</label>
                        <input
                          type="number" min="1"
                          className="pm-number-input"
                          value={configDraft.edn_ticket_topic_id}
                          placeholder={t('parcelMonitor.config.ednTopicIdPlaceholder', 'ex : 12 — requis pour créer le ticket côté EDN')}
                          onChange={(e) => markConfigDirty({ edn_ticket_topic_id: e.target.value })}
                        />
                      </>
                    )}
                  </div>
                )}

                <div className="pm-form-row">
                  <label>{t('parcelMonitor.config.threshold', 'Seuil de signalement')}</label>
                  <select
                    value={configDraft.flag_threshold}
                    onChange={(e) => markConfigDirty({ flag_threshold: e.target.value })}
                  >
                    <option value="">{t('parcelMonitor.config.thresholdDefault', 'Défaut ({{value}})', { value: config.default_flag_threshold })}</option>
                    {THRESHOLD_OPTIONS.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                    {configDraft.flag_threshold !== ''
                      && !THRESHOLD_OPTIONS.includes(configDraft.flag_threshold) && (
                      <option value={configDraft.flag_threshold}>{configDraft.flag_threshold}</option>
                    )}
                  </select>
                  <p className="pm-hint">
                    {t('parcelMonitor.config.thresholdHint',
                      'Plus le seuil est bas, plus de colis sont signalés (précision plus faible).')}
                  </p>
                </div>

                <div className="pm-form-row">
                  <label>{t('parcelMonitor.config.smsMoved', 'SMS au destinataire')}</label>
                  <p className="pm-hint">
                    {t('parcelMonitor.config.smsMovedHint',
                      'Retard détecté, colis reparti, colis livré : les SMS aux destinataires se règlent dans Notifications SMS.')}
                    {' '}<Link to="/sms-notifications">{t('parcelMonitor.config.smsOpen', 'Ouvrir Notifications SMS')}</Link>
                  </p>
                </div>
              </div>

              <div className="pm-form-actions">
                {configDirty && <span className="pm-dirty-hint">{t('parcelMonitor.unsavedChanges', 'Modifications non enregistrées')}</span>}
                <button
                  type="button" className="u-btn u-btn--primary"
                  disabled={configSaving || !configDirty}
                  onClick={saveConfig}
                >
                  {configSaving ? (<><FaSpinner className="pm-spin" /> {t('common.saving', 'Enregistrement…')}</>) : t('common.save', 'Enregistrer')}
                </button>
              </div>

              {/* ---- where parcels come from: the orders (API, Shopify, PrestaShop) ---- */}
              <div className="pm-api-section">
                <h3><FaPlug /> {t('parcelMonitor.sources.title', 'Arrivée des colis')}</h3>
                <p className="pm-hint pm-hint--wide">
                  {t('parcelMonitor.sources.hint',
                    'Les colis arrivent avec vos commandes : chaque numéro de suivi d’une commande (API, Shopify, PrestaShop) est surveillé ici dès que la surveillance est active.')}
                </p>
                <div className="pm-source-links">
                  <Link className="u-btn u-btn--secondary" to="/commerce/orders?tab=connections">{t('parcelMonitor.sources.shops', 'Connecter Shopify / PrestaShop')}</Link>
                  <Link className="u-btn u-btn--secondary" to="/commerce/orders?tab=api">{t('parcelMonitor.sources.api', 'Clés et documentation de l’API commandes')}</Link>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ---- toolbar ---- */}
      <div className="pm-toolbar">
        <div className="pm-search">
          <FaSearch />
          <input
            type="text"
            value={searchInput}
            placeholder={t('parcelMonitor.searchPlaceholder', 'Rechercher un numéro de suivi…')}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => updateListParams({ status: e.target.value, page: '' })}
        >
          <option value="">{t('parcelMonitor.filters.allStatuses', 'Tous les statuts')}</option>
          {STATUS_VALUES.map((value) => (
            <option key={value} value={value}>{statusLabel(value)}</option>
          ))}
        </select>
        <label className="pm-flag-filter">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => updateListParams({ flagged: e.target.checked ? '1' : '', page: '' })}
          />
          {t('parcelMonitor.filters.flaggedOnly', 'Colis signalés uniquement')}
        </label>
        <button type="button" className="u-btn u-btn--secondary" onClick={() => { loadList(); loadStats(); }}>
          <FaSync /> {t('parcelMonitor.refresh', 'Actualiser')}
        </button>
      </div>

      {/* ---- table ---- */}
      <div className="pm-card">
        <div className="pm-table-scroll">
          <table className="pm-table">
            <colgroup>
              <col style={{ width: '160px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '170px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('parcelMonitor.columns.tracking', 'N° de suivi')}</th>
                <th>{t('parcelMonitor.columns.carrier', 'Transporteur')}</th>
                <th>{t('parcelMonitor.columns.destination', 'Destination')}</th>
                <th>{t('parcelMonitor.columns.status', 'Statut')}</th>
                <th>{t('parcelMonitor.columns.lastEvent', 'Dernier événement')}</th>
                <th>{t('parcelMonitor.columns.risk', 'Risque')}</th>
                <th>{t('parcelMonitor.columns.alert', 'Alerte')}</th>
                <th>{t('parcelMonitor.columns.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && [...Array(6)].map((_, idx) => (
                <tr key={idx} className="pm-skeleton-row">
                  {[...Array(8)].map((__, cell) => <td key={cell}><span className="pm-skeleton" /></td>)}
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="pm-empty">
                    {urlQuery || statusFilter || flaggedOnly
                      ? t('common.noSearchResults', 'Aucun résultat pour cette recherche.')
                      : t('parcelMonitor.empty', "Aucun colis suivi pour le moment. Créez une clé API et poussez vos colis pour démarrer la surveillance.")}
                  </td>
                </tr>
              )}
              {!loading && items.map((parcel) => (
                <React.Fragment key={parcel.id}>
                  <tr
                    className={`pm-row ${parcel.risk?.flagged ? 'pm-row--flagged' : ''}`}
                    role="button" tabIndex={0}
                    onClick={() => toggleExpand(parcel.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(parcel.id); } }}
                  >
                    <td className="pm-mono" title={parcel.tracking_number}>{parcel.tracking_number}</td>
                    <td title={parcel.carrier_label || parcel.carrier_key}>{parcel.carrier_label || parcel.carrier_key}</td>
                    <td title={[parcel.to_city, parcel.to_postcode].filter(Boolean).join(' ')}>
                      {parcel.to_city
                        ? `${parcel.to_city}${parcel.to_postcode ? ` (${parcel.to_postcode})` : ''}`
                        : (parcel.to_postcode || '—')}
                    </td>
                    <td><span className={`pm-status pm-status--${parcel.status}`}>{statusLabel(parcel.status)}</span></td>
                    <td title={parcel.provider_status || ''}>{formatDate(parcel.last_event_at)}</td>
                    <td>{riskBadge(parcel.risk)}</td>
                    <td>{alertBadge(parcel.alert)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {parcel.alert?.state === 'open' && (
                        <button
                          type="button" className="u-btn u-btn--secondary pm-btn-sm"
                          disabled={actionBusy === parcel.id + 'acknowledge'}
                          onClick={() => alertAction(parcel.id, 'acknowledge')}
                        >{t('parcelMonitor.actions.acknowledge', 'Prendre en compte')}</button>
                      )}
                      {['open', 'acknowledged'].includes(parcel.alert?.state) && (
                        <button
                          type="button" className="u-btn u-btn--secondary pm-btn-sm"
                          disabled={actionBusy === parcel.id + 'resolve'}
                          onClick={() => { setResolveTarget(parcel.id); setResolveReason('false_alarm'); setResolveNote(''); }}
                        >{t('parcelMonitor.actions.resolve', 'Résoudre')}</button>
                      )}
                    </td>
                  </tr>
                  {expandedId === parcel.id && (
                    <tr className="pm-detail-row">
                      <td colSpan={8}>
                        {detailLoading && <p className="pm-muted"><FaSpinner className="pm-spin" /> {t('common.loading', 'Chargement…')}</p>}
                        {!detailLoading && expandedDetail && (
                          <div className="pm-detail">
                            {expandedDetail.tracking_provider === 'edn' && !['delivered', 'returned'].includes(expandedDetail.status) && (
                              <div className="pm-detail-toolbar">
                                <button
                                  type="button" className="u-btn u-btn--secondary pm-btn-sm"
                                  disabled={actionBusy === parcel.id + 'refresh'}
                                  onClick={() => refreshParcelTracking(parcel.id)}
                                >
                                  {actionBusy === parcel.id + 'refresh'
                                    ? (<><FaSpinner className="pm-spin" /> {t('parcelMonitor.detail.refreshing', 'Actualisation…')}</>)
                                    : (<><FaSync /> {t('parcelMonitor.detail.refreshTracking', 'Actualiser le suivi')}</>)}
                                </button>
                              </div>
                            )}
                            <div className="pm-detail-grid">
                              <div className="pm-detail-block">
                                <h4>{t('parcelMonitor.detail.parcelBlock', 'Colis')}</h4>
                                <dl>
                                  {expandedDetail.order_ref && (<><dt>{t('parcelMonitor.detail.orderRef', 'Réf. commande')}</dt><dd><Link to={`/commerce/orders?q=${encodeURIComponent(expandedDetail.order_ref)}`}>{expandedDetail.order_ref}</Link></dd></>)}
                                  {expandedDetail.merchant_ref && expandedDetail.merchant_ref !== 'unknown' && (<><dt>{t('parcelMonitor.detail.merchant', 'Expéditeur')}</dt><dd>{expandedDetail.merchant_ref}</dd></>)}
                                  {expandedDetail.weight_kg != null && (<><dt>{t('parcelMonitor.detail.weight', 'Poids')}</dt><dd>{expandedDetail.weight_kg} kg</dd></>)}
                                  {expandedDetail.dimensions_cm && (
                                    <><dt>{t('parcelMonitor.detail.dimensions', 'Dimensions')}</dt>
                                    <dd>{['length_cm', 'width_cm', 'height_cm'].map((k) => expandedDetail.dimensions_cm[k]).filter((v) => v != null).join(' × ')} cm</dd></>
                                  )}
                                  {expandedDetail.order_value != null && expandedDetail.order_value > 0 && (<><dt>{t('parcelMonitor.detail.orderValue', 'Valeur')}</dt><dd>{expandedDetail.order_value.toFixed(2)} €</dd></>)}
                                  <dt>{t('parcelMonitor.detail.insurance', 'Assurance')}</dt>
                                  <dd>{expandedDetail.insurance ? t('common.yes', 'Oui') : t('common.no', 'Non')}</dd>
                                  {expandedDetail.content && (<><dt>{t('parcelMonitor.detail.content', 'Contenu')}</dt><dd>{expandedDetail.content}</dd></>)}
                                  {expandedDetail.product_category && (<><dt>{t('parcelMonitor.detail.productCategory', 'Type de produit')}</dt><dd>{expandedDetail.product_category}</dd></>)}
                                  {expandedDetail.delivery_mode && (<><dt>{t('parcelMonitor.detail.deliveryMode', 'Mode de livraison')}</dt><dd>{expandedDetail.delivery_mode}</dd></>)}
                                </dl>
                              </div>
                              <div className="pm-detail-block">
                                <h4>{t('parcelMonitor.detail.recipientBlock', 'Destinataire')}</h4>
                                <dl>
                                  {expandedDetail.recipient?.name && (<><dt>{t('parcelMonitor.detail.recipientName', 'Nom')}</dt><dd>{expandedDetail.recipient.name}</dd></>)}
                                  {expandedDetail.recipient?.phone && (<><dt>{t('parcelMonitor.detail.recipientPhone', 'Téléphone')}</dt><dd>{expandedDetail.recipient.phone}</dd></>)}
                                  {expandedDetail.recipient?.email && (<><dt>{t('parcelMonitor.detail.recipientEmail', 'Email')}</dt><dd>{expandedDetail.recipient.email}</dd></>)}
                                  {expandedDetail.to_address && (
                                    <><dt>{t('parcelMonitor.detail.deliveryAddress', 'Adresse de livraison')}</dt>
                                    <dd>{[expandedDetail.to_address.company, expandedDetail.to_address.street,
                                      [expandedDetail.to_address.postcode, expandedDetail.to_address.city].filter(Boolean).join(' '),
                                      expandedDetail.to_address.country].filter(Boolean).join(', ')}</dd></>
                                  )}
                                  {expandedDetail.relay_point && (
                                    <><dt>{t('parcelMonitor.detail.relayPoint', 'Point relais')}</dt>
                                    <dd>{[expandedDetail.relay_point.name, expandedDetail.relay_point.street,
                                      [expandedDetail.relay_point.postcode, expandedDetail.relay_point.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</dd>
                                    <dt>{t('parcelMonitor.detail.relayPointId', 'ID point relais')}</dt>
                                    <dd>{expandedDetail.relay_point.id
                                      || <span className="pm-muted">{t('parcelMonitor.detail.notProvided', 'Non fourni')}</span>}</dd></>
                                  )}
                                </dl>
                              </div>
                              <div className="pm-detail-block">
                                <h4>{t('parcelMonitor.detail.riskBlock', 'Évaluation du risque')}</h4>
                                <dl>
                                  <dt>{t('parcelMonitor.detail.stallScore', 'Score de ralentissement')}</dt>
                                  <dd>{expandedDetail.risk?.stall_score != null
                                    ? `${(expandedDetail.risk.stall_score * 100).toFixed(0)}%`
                                    : t('parcelMonitor.risk.awaitingFirstScan', 'En attente du 1er suivi')}</dd>
                                  <dt>{t('parcelMonitor.detail.hazardScoreLabel', 'Score de silence')}</dt>
                                  <dd>{expandedDetail.risk?.hazard_score != null
                                    ? `${(expandedDetail.risk.hazard_score * 100).toFixed(0)}% (${expandedDetail.risk.checkpoint_h}h)`
                                    : t('parcelMonitor.detail.hazardNotYet', 'Calculé après 6h sans événement')}</dd>
                                  {expandedDetail.risk?.flag_reason && (<><dt>{t('parcelMonitor.detail.flagReason', 'Motif du signalement')}</dt><dd>{expandedDetail.risk.flag_reason}</dd></>)}
                                  {expandedDetail.outcome?.label && (
                                    <><dt>{t('parcelMonitor.detail.outcome', 'Résultat final')}</dt>
                                    <dd>{t(`parcelMonitor.outcome.${expandedDetail.outcome.label}`, expandedDetail.outcome.label)}
                                      {expandedDetail.outcome.false_alarm && ` — ${t('parcelMonitor.outcome.falseAlarmTag', 'fausse alerte enregistrée')}`}</dd></>
                                  )}
                                  {expandedDetail.alert?.resolution_reason && (
                                    <><dt>{t('parcelMonitor.detail.resolutionReason', 'Résolution humaine')}</dt>
                                    <dd>{t(`parcelMonitor.resolve.${{
                                      false_alarm: 'falseAlarm', resolved_by_action: 'resolvedByAction',
                                      confirmed_problem: 'confirmedProblem', other: 'other',
                                    }[expandedDetail.alert.resolution_reason]}`, expandedDetail.alert.resolution_reason)}
                                      {expandedDetail.alert.resolution_note && ` — ${expandedDetail.alert.resolution_note}`}</dd></>
                                  )}
                                  {expandedDetail.alert?.sms_sent_at && (<><dt>{t('parcelMonitor.detail.smsSent', 'SMS destinataire envoyé')}</dt><dd>{formatDate(expandedDetail.alert.sms_sent_at)}</dd></>)}
                                </dl>
                              </div>
                              {expandedDetail.alert?.actions && (
                                <div className="pm-detail-block">
                                  <h4>{t('parcelMonitor.detail.actionsBlock', 'Actions automatiques')}</h4>
                                  <dl>
                                    {expandedDetail.alert.actions.client?.name && (
                                      <><dt>{t('parcelMonitor.detail.actionClient', 'Client')}</dt>
                                      <dd>{expandedDetail.alert.actions.client.name}</dd></>
                                    )}
                                    <dt>{t('parcelMonitor.detail.actionTicket', 'Ticket')}</dt>
                                    <dd>
                                      {expandedDetail.alert.actions.ticket_id
                                        ? (
                                          <a className="pm-ticket-link" href={`/tickets/${expandedDetail.alert.actions.ticket_id}/messages`}>
                                            {t('parcelMonitor.detail.openTicket', 'Ouvrir le ticket')}
                                          </a>
                                        )
                                        : <span className="pm-muted">—</span>}
                                    </dd>
                                    {expandedDetail.alert.actions.external && (
                                      <><dt>{t('parcelMonitor.detail.actionExternal', 'Système externe')}</dt>
                                      <dd>
                                        {expandedDetail.alert.actions.external.integration_key?.toUpperCase()}{' — '}
                                        {expandedDetail.alert.actions.external.state === 'created'
                                          ? `${t('parcelMonitor.detail.externalCreated', 'ticket créé')} ${expandedDetail.alert.actions.external.external_ticket_id ? `#${expandedDetail.alert.actions.external.external_ticket_id}` : ''}`
                                          : (expandedDetail.alert.actions.external.reason || expandedDetail.alert.actions.external.state)}
                                      </dd></>
                                    )}
                                    {expandedDetail.alert.actions.carrier && (
                                      <><dt>{t('parcelMonitor.detail.actionCarrier', 'Contact transporteur')}</dt>
                                      <dd>{{
                                        contacted: t('parcelMonitor.detail.carrierContacted', 'Demande envoyée'),
                                        pending_validation: t('parcelMonitor.detail.carrierPending', 'En attente de validation'),
                                        already_contacted: t('parcelMonitor.detail.carrierAlready', 'Déjà contacté'),
                                        skipped: expandedDetail.alert.actions.carrier.reason || t('parcelMonitor.detail.carrierSkipped', 'Ignoré'),
                                        error: expandedDetail.alert.actions.carrier.reason || t('common.error', 'Erreur'),
                                      }[expandedDetail.alert.actions.carrier.state] || expandedDetail.alert.actions.carrier.state}</dd></>
                                    )}
                                    {expandedDetail.alert.actions.error && (
                                      <><dt>{t('common.error', 'Erreur')}</dt>
                                      <dd className="pm-action-error">{expandedDetail.alert.actions.error}</dd></>
                                    )}
                                  </dl>
                                </div>
                              )}
                            </div>
                            <ul className="pm-timeline">
                              {[...(expandedDetail.events || [])].reverse().map((event, idx) => (
                                <li key={idx}>
                                  <span className="pm-timeline-date">{formatDate(event.event_date)}</span>
                                  <span className={`pm-timeline-type pm-timeline-type--${event.event_type === 'DEL' ? 'del' : ['INC', 'RET', 'RETD', 'CARD', 'CUSD'].includes(event.event_type) ? 'inc' : 'std'}`}>{event.event_type}</span>
                                  <span>{event.status_text}</span>
                                </li>
                              ))}
                              {(expandedDetail.events || []).length === 0 && (
                                <li className="pm-muted">{t('parcelMonitor.detail.noEvents', 'Aucun événement de suivi pour le moment.')}</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pm-table-footer">
          <span className="pm-muted">
            {t('parcelMonitor.pager.total', '{{count}} colis', { count: total })}
          </span>
          <div className="pm-pager">
            {pageItems.map((item, idx) => item === '…'
              ? <span key={`e${idx}`} className="pm-pager-ellipsis">…</span>
              : (
                <button
                  key={item} type="button"
                  className={`pm-pager-btn ${item === page ? 'active' : ''}`}
                  onClick={() => updateListParams({ page: item === 1 ? '' : item })}
                >{item}</button>
              ))}
          </div>
          <select value={size} onChange={(e) => updateListParams({ size: e.target.value === '25' ? '' : e.target.value, page: '' })}>
            {PAGE_SIZES.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
      </div>

      {/* ---- resolve dialog: reason is mandatory (precision feedback loop) ---- */}
      {resolveTarget && (
        <div className="pm-modal-overlay" role="dialog" aria-modal="true">
          <div className="pm-modal">
            <h3>{t('parcelMonitor.resolve.title', "Résoudre l'alerte")}</h3>
            <p className="pm-hint">
              {t('parcelMonitor.resolve.hint',
                'Indiquez pourquoi : cette information mesure la précision des modèles et alimente leur réentraînement.')}
            </p>
            <label className="pm-sublabel">{t('parcelMonitor.resolve.reason', 'Motif')}</label>
            <select value={resolveReason} onChange={(e) => setResolveReason(e.target.value)}>
              <option value="false_alarm">{t('parcelMonitor.resolve.falseAlarm', 'Fausse alerte — le colis va bien')}</option>
              <option value="resolved_by_action">{t('parcelMonitor.resolve.resolvedByAction', 'Résolu par notre intervention (transporteur/client contacté)')}</option>
              <option value="confirmed_problem">{t('parcelMonitor.resolve.confirmedProblem', 'Problème confirmé (perdu, endommagé, retourné…)')}</option>
              <option value="other">{t('parcelMonitor.resolve.other', 'Autre')}</option>
            </select>
            <label className="pm-sublabel">{t('parcelMonitor.resolve.note', 'Note (optionnel)')}</label>
            <textarea
              maxLength={500} rows={3}
              value={resolveNote}
              placeholder={t('parcelMonitor.resolve.notePlaceholder', 'Contexte utile : ce qui a été constaté ou fait…')}
              onChange={(e) => setResolveNote(e.target.value)}
            />
            <div className="pm-modal-actions">
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setResolveTarget(null)}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button" className="u-btn u-btn--primary"
                disabled={actionBusy === resolveTarget + 'resolve'}
                onClick={async () => {
                  await alertAction(resolveTarget, 'resolve', { reason: resolveReason, note: resolveNote || null });
                  setResolveTarget(null);
                }}
              >{t('parcelMonitor.actions.resolve', 'Résoudre')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParcelMonitor;
